import bellIcon from "../../assets/icons/bell.svg";
import briefcaseIcon from "../../assets/icons/briefcase.svg";
import clipboardIcon from "../../assets/icons/clipboard.svg";
import magnifierIcon from "../../assets/icons/magnifier.svg";
import type { LandingCategory } from "../../components/landing/LandingCategoryShowcase";
import type { LandingGlyphName } from "../../components/landing/LandingGlyph";
import type { LandingStat } from "../../components/landing/LandingStatBand";
import type { LandingTestimonial } from "../../components/landing/LandingTestimonials";
import type { AbstractCard } from "../../components/ui/AbstractCards/AbstractCards";
import type { FlowChartStep } from "../../components/ui/FlowChart/FlowChart";
import { companyName } from "../../constants/constants";

export type RoleButton = {
  label: string;
  subheading: string;
  signupRole: "TEEN" | "ADULT";
  /** Preselects and locks the business account type on the signup form. */
  signupAccountType?: "BUSINESS";
  iconSrc: string;
  iconAlt: string;
};

export type HeroGigCard = {
  title: string;
  meta: string;
  pay: string;
  toneClassName: string;
  positionClassName: string;
  liveDataSlot?: number;
};

export const roleButtons: RoleButton[] = [
  {
    label: "Find a Gig",
    subheading: "For teens looking for work",
    signupRole: "TEEN",
    iconSrc: magnifierIcon,
    iconAlt: "Find gigs",
  },
  {
    label: "Post a Gig",
    subheading: "For people and businesses seeking help",
    signupRole: "ADULT",
    iconSrc: briefcaseIcon,
    iconAlt: "Post gigs",
  },
  {
    label: "Post a Business Listing",
    subheading: "For businesses hiring ongoing teen help",
    signupRole: "ADULT",
    signupAccountType: "BUSINESS",
    iconSrc: clipboardIcon,
    iconAlt: "Post business listings",
  },
  {
    label: "Manage Teen Accounts",
    subheading: "For parents/guardians overseeing teen accounts",
    signupRole: "ADULT",
    iconSrc: bellIcon,
    iconAlt: "Guardian oversight",
  },
];

export const heroTrustSignals: { title: string; body: string; glyph: LandingGlyphName }[] = [
  {
    title: "Parents stay involved",
    body: "Guardian visibility and approvals are built around under-18 teen accounts.",
    glyph: "shield",
  },
  {
    title: "Pay is clear up front",
    body: "Teens see the gig details, expectations, and pay terms before applying.",
    glyph: "card",
  },
  {
    title: "Structured communication",
    body: "Messages and updates stay connected to the gig so everyone has context.",
    glyph: "chat",
  },
];

export const landingStats: LandingStat[] = [
  {
    value: "100%",
    label: "Of agreed pay to the teen",
    detail: "MyTeenGig earns from posting fees, not from a teen's pay.",
    glyph: "card",
  },
  {
    value: "Stripe",
    label: "Handles the money",
    detail: "Gig funding and payouts run on Stripe's secure platform.",
    glyph: "shield",
  },
  {
    value: "Every gig",
    label: "Has a guardian view",
    detail: "Parents/guardians see gig details and can approve participation.",
    glyph: "clock",
  },
];

export const landingCategories: LandingCategory[] = [
  {
    name: "Yard Work",
    blurb: "Leaf cleanup, mowing, mulch, and seasonal outdoor help.",
    glyph: "leaf",
  },
  {
    name: "Dog Walking & Pet Care",
    blurb: "Neighborhood walks, feeding, and short pet-sitting windows.",
    glyph: "paw",
  },
  {
    name: "Tech Help",
    blurb: "Device setup, printers, photo backups, and smart-home basics.",
    glyph: "laptop",
  },
  {
    name: "Babysitting & Mother's Helper",
    blurb: "Extra hands at home while a parent/guardian is nearby.",
    glyph: "heart",
  },
  {
    name: "Errands",
    blurb: "Pickups, drop-offs, and quick local runs that free up a day.",
    glyph: "bag",
  },
  {
    name: "Event & Party Help",
    blurb: "Setup, serving, and teardown for graduations and gatherings.",
    glyph: "balloon",
  },
  {
    name: "Tutoring & Homework Help",
    blurb: "Reading, math, and study support for younger students.",
    glyph: "book",
  },
  {
    name: "Car Washing",
    blurb: "Wash, vacuum, and detail work done in the driveway.",
    glyph: "car",
  },
  {
    name: "Cleaning & Household Help",
    blurb: "Straightening up, laundry loads, and light indoor chores.",
    glyph: "grid",
  },
  {
    name: "Moving & Furniture Help",
    blurb: "Box hauling, flat-pack assembly, and garage reorganizing.",
    glyph: "bag",
  },
  {
    name: "Elder Care Support",
    blurb: "Company, light help around the house, and simple errands.",
    glyph: "shield",
  },
  {
    name: "Business & Volunteer Shifts",
    blurb: "Weekend retail, food service, and community volunteer hours.",
    glyph: "clock",
  },
];

/**
 * Real, permissioned 5-star reviews pulled from production `gig_reviews`
 * (see realReviews.json): two from gig posters reviewing teen workers and two
 * from teen workers reviewing their posters.
 */
export const landingTestimonials: LandingTestimonial[] = [
  {
    id: "review-poster-dj-birthday",
    rating: 5,
    message:
      "Marco and his partner were amazing! They arrived early, set up quickly, had great attitudes and positive energy. We were very happy with them and would happily recommend them! This was our first time using My Teen Gig and it was a great experience!",
    authorName: "Dana K.",
    authorRole: "Gig poster",
    gigTitle: "DJ for a kids birthday party",
    location: "Port Washington, NY",
  },
  {
    id: "review-poster-yard-piles",
    rating: 5,
    message:
      "Nick showed up on time and was ready to work. He worked efficiently and in a timely matter. He went above and beyond to bring the many garbage bags to my curb. I highly recommend Nick.",
    authorName: "Sharon S.",
    authorRole: "Gig poster",
    gigTitle: "Pick up raked yard piles",
    location: "Wantagh, NY",
  },
  {
    id: "review-teen-top-soil",
    rating: 5,
    message: "They were the nicest people! Communication was perfect. So glad I was able to help them out.",
    authorName: "Nick S.",
    authorRole: "Teen worker",
    gigTitle: "Top Soil - moving & filling",
    location: "Ronkonkoma, NY",
  },
  {
    id: "review-teen-lawn-mowing",
    rating: 5,
    message: "Gig was organized easily, did what I needed to do, got paid. Easy as that.",
    authorName: "Marco R.",
    authorRole: "Teen worker",
    gigTitle: "Lawn mowing",
    location: "Massapequa Park, NY",
  },
];

/**
 * Hardcoded platform-wide review average. Source: production `gig_reviews`
 * snapshot in realReviews.json (42 reviews, all 5.0). Refresh alongside that file.
 */
export const landingReviewsSummary = {
  averageRating: 5,
  totalReviews: 42,
};

export const heroGigCards: HeroGigCard[] = [
  {
    title: "Dog walking",
    meta: "Today after school",
    pay: "$52",
    toneClassName: "border-[#14B8A6]/35 bg-[#ECFDF5]/90 text-[#065F46]",
    positionClassName: "landing-gig-card landing-gig-card-a left-[5%] top-[24%] hidden w-60 xl:block",
    liveDataSlot: 1,
  },
  {
    title: "Event setup",
    meta: "Saturday morning",
    pay: "$65",
    toneClassName: "border-[#F97316]/35 bg-[#FFF7ED]/90 text-[#9A3412]",
    positionClassName: "landing-gig-card landing-gig-card-b right-[6%] top-[24%] hidden w-64 xl:block",
    liveDataSlot: 0,
  },
  {
    title: "Tech help",
    meta: "Remote or nearby",
    pay: "$55",
    toneClassName: "border-myteengig-primary/30 bg-white/90 text-myteengig-text-primary",
    positionClassName: "landing-gig-card landing-gig-card-c left-[3%] top-[43%] hidden w-64 xl:block",
    liveDataSlot: 2,
  },
  {
    title: "Yard cleanup",
    meta: "2.3 mi away",
    pay: "$58",
    toneClassName: "border-[#EAB308]/40 bg-[#FEFCE8]/90 text-[#854D0E]",
    positionClassName: "landing-gig-card landing-gig-card-d right-[3%] top-[43%] hidden w-60 xl:block",
    liveDataSlot: 3,
  },
];

export const heroTickerItems = [
  "Yard work",
  "Dog walking",
  "Pet sitting",
  "Snow shoveling",
  "Tech help",
  "Babysitting",
  "Mother's helper",
  "Tutoring",
  "Homework help",
  "Local errands",
  "Event help",
  "Party setup",
  "Car washing",
  "House cleaning",
  "Moving help",
  "Furniture assembly",
  "Elder care",
  "Sports training",
  "Retail support",
  "Restaurant help",
  "Warehouse & stock",
  "Delivery runs",
  "Social media help",
  "Volunteer gigs",
];

export const howItWorks: FlowChartStep[] = [
  {
    title: "1. Homeowners and Businesses Post a Gig",
    body: "Homeowners and businesses post a paid gig or volunteer opportunity and describe the help they need.",
    imageSrc: briefcaseIcon,
    imageAlt: "Post a gig step",
  },
  {
    title: "2. Teens Apply for the Gig",
    body: "Teens browse nearby opportunities and apply for the gigs that fit their schedule.",
    imageSrc: clipboardIcon,
    imageAlt: "Teens apply step",
  },
  {
    title: "3. Parents/Guardians Are Notified",
    body: "Parents/guardians can view applications, stay informed, and approve participation when needed.",
    imageSrc: bellIcon,
    imageAlt: "Parent awareness step",
  },
  {
    title: "4. The Work Gets Done",
    body: "Once a teen is accepted, the agreed-upon work gets done and payment can be released after completion.",
    imageSrc: magnifierIcon,
    imageAlt: "Work happens step",
  },
];

export const navItems = [
  { href: "#how-it-works", label: "How It Works" },
  { href: "#gig-types", label: "Gig Types" },
  { href: "#reviews", label: "Reviews" },
  { href: "#about", label: "About" },
];

export const audienceCards: AbstractCard[] = [
  {
    title: "For Teens",
    items: [
      "Build real-world experience",
      "Earn money locally",
      "Choose gigs that fit your schedule",
      "Stay within safe, approved categories",
    ],
    ctaLabel: "Explore Teen Gigs",
    ctaVariant: "primary",
  },
  {
    title: "For Parents/Guardians",
    items: [
      "Receive notifications",
      "Approve certain gigs",
      "Understand details before participation",
      "Feel confident in safety guidelines",
    ],
    ctaLabel: "Learn About Parent/Guardian Controls",
    ctaVariant: "secondary",
  },
  {
    title: "For Homeowners and Businesses",
    items: [
      "Access motivated local teens",
      "Fill short-term help needs",
      "Post clearly defined gigs",
    ],
    ctaLabel: "Post a Gig",
    ctaVariant: "outline",
  },
];

export const interestFormUrl =
  "https://docs.google.com/forms/d/e/1FAIpQLSclogS8u2ougHJ5j_en6lD73Bx6VymtYwAHQ48ZezPSgU7dgQ/viewform?usp=dialog";

export const interestFormHighlights = [
  `Get launch updates for ${companyName}`,
  "Tell us if you're a teen, parent, or employer",
  "Help shape early features and safety tools",
];
